<template>
  <div v-if="visible" class="vnc-panel">
    <el-alert type="info" :closable="false" show-icon>
      <template #title>远程桌面已在独立窗口打开</template>
      <p class="tip">
        请在<strong>弹出的浏览器窗口</strong>中扫码登录；本对话框仅显示登录进度，不再内嵌小窗。
      </p>
    </el-alert>
    <div class="vnc-actions">
      <el-button type="primary" @click="() => openLargeScreen(false)">
        {{ largeWinOpen ? '聚焦独立大屏' : '打开独立大屏' }}
      </el-button>
      <el-button @click="() => openLargeScreen(true)">重新打开独立大屏</el-button>
    </div>
    <p v-if="largeWinOpen" class="status-ok">独立大屏已连接，可直接扫码</p>
    <p v-else class="status-warn">若未看到弹窗，请允许浏览器弹窗后点「打开独立大屏」</p>
  </div>
</template>

<script setup>
import { watch, ref, nextTick, onBeforeUnmount, computed } from 'vue'
import { ElMessage } from 'element-plus'

import { DEFAULT_VNC_VIEWER_PATH, DEFAULT_VNC_WS_PATH } from '../utils/appBase'

const props = defineProps({
  visible: { type: Boolean, default: false },
  wsPath: { type: String, default: DEFAULT_VNC_WS_PATH },
  viewerPath: { type: String, default: DEFAULT_VNC_VIEWER_PATH },
  autoLargeScreen: { type: Boolean, default: true },
})

const autoOpenedOnce = ref(false)
const largeWinRef = ref(null)

const buildViewerUrl = () => {
  const ws = props.wsPath || DEFAULT_VNC_WS_PATH
  const viewer = new URL(props.viewerPath || DEFAULT_VNC_VIEWER_PATH, window.location.href)
  const q = new URLSearchParams({
    path: ws,
    standalone: '1',
    fullscreen: '1',
    t: String(Date.now()),
  })
  viewer.search = q.toString()
  return viewer.toString()
}

const largeWinOpen = computed(() => {
  try {
    return !!(largeWinRef.value && !largeWinRef.value.closed)
  } catch {
    return false
  }
})

/** @param forceNew 为 true 时始终新开窗口 */
const openLargeScreen = (forceNew = false) => {
  try {
    if (!forceNew && largeWinRef.value && !largeWinRef.value.closed) {
      largeWinRef.value.focus()
      return true
    }
  } catch {
    largeWinRef.value = null
  }

  const url = buildViewerUrl()
  const sw = window.screen?.availWidth || 1280
  const sh = window.screen?.availHeight || 800
  const w = Math.min(sw - 24, 1440)
  const h = Math.min(sh - 48, 960)
  const left = Math.max(0, Math.round((sw - w) / 2))
  const top = Math.max(0, Math.round((sh - h) / 2))
  const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no`
  const win = window.open(url, `mp_vnc_${Date.now()}`, features)
  if (!win) {
    ElMessage.warning('独立大屏被浏览器拦截，请允许弹窗后重试')
    return false
  }
  largeWinRef.value = win
  return true
}

const closeLargeScreen = () => {
  try {
    if (largeWinRef.value && !largeWinRef.value.closed) {
      largeWinRef.value.close()
    }
  } catch {
    /* ignore */
  }
  largeWinRef.value = null
}

const resetAutoOpen = () => {
  autoOpenedOnce.value = false
}

watch(
  () => props.visible,
  (v) => {
    if (!v) {
      autoOpenedOnce.value = false
      closeLargeScreen()
      return
    }
    if (!props.autoLargeScreen || autoOpenedOnce.value) return
    autoOpenedOnce.value = true
    nextTick(() => openLargeScreen(false))
  },
)

onBeforeUnmount(() => {
  closeLargeScreen()
})

defineExpose({ openLargeScreen, closeLargeScreen, resetAutoOpen })
</script>

<style scoped>
.vnc-panel { margin-top: 8px; }
.tip { margin: 6px 0 0; font-size: 13px; line-height: 1.5; color: #606266; }
.vnc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.status-ok { margin: 10px 0 0; font-size: 13px; color: #67c23a; }
.status-warn { margin: 10px 0 0; font-size: 13px; color: #e6a23c; }
</style>
